import { Component, OnInit, Input } from '@angular/core';
import { CdkDragDrop, transferArrayItem } from '@angular/cdk/drag-drop';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { VersionStoreService, VersionStoreDialogComponent, VersionListDialogComponent, VersionManagementDialogComponent, VersionDialogMode } from '../..';
import { VersionStoreSortDialogComponent } from '../version-store-sort-dialog/version-store-sort-dialog.component';
import { VersionSearchDialogComponent } from '../version-search-dialog/version-search-dialog.component';
import { EventService, VersionService, Event, OfferPromo, PromoDomain, Version, Store, VERSION_STORE_PAGE_SIZE } from '../..';
import { catchError, combineLatest, map, Observable, of, retry, switchMap, take, tap } from 'rxjs';
import { ConfirmDialogComponent, DEFAULT_SNACKBAR_CONFIG, Logger } from 'src/app/@shared';
import { MatSnackBar } from '@angular/material/snack-bar';
import { FormControl, FormGroup } from '@angular/forms';
import { PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';

const log = new Logger('VersionsListComponent');

@Component({
  selector: 'app-versions',
  templateUrl: './versions-list.component.html',
  styleUrls: ['./versions-list.component.scss']
})
export class VersionsListComponent<TVersion extends Version, TEvent extends Event, TOfferPromo extends OfferPromo, TPromoDomain extends PromoDomain<OfferPromo>> implements OnInit {
  @Input() version: any;
  showUnassignedPanel = true;
  hideUnassignedPanel = false;
  displayedCount = VERSION_STORE_PAGE_SIZE;
  sortBy: 'VersionName' | 'ClientKey' = 'VersionName';
  sortDirection: 'asc' | 'desc' = 'asc';
  versions: Version[] = [];
  allStores: Store[] = [];
  eventId!: string;
  unassignedStores: Store[] = [];
  totalUnassignedRecords = 0;
  unassignedPage = 0;

  filter = {
    versions: [] as string[],
    stores: [] as string[]
  };
  filtersForm = new FormGroup({
    search: new FormControl(),
  });

  noResults = false;

  get connectedLists(): string[] {
    return [
      ...this.versions.map((_, i) => `version-${i}`), // all versions
      'unassigned'
    ];
  }

  viewModel$ = combineLatest([
    this.versionService.versionsWithStores$,
    this.versionService.isLoading$,
    this.versionService.totalRecords$,
    this.versionService.page$,
    this.eventService.getEvent(this.route.snapshot.params['eventId'] || this.versionService.eventId)
  ]).pipe(
    map(([versions, isLoading, totalRecords, page, event]) => {
      if (versions.length === 0 && page.pageIndex > 0) {
        page.previousPageIndex = 0;
        page.pageIndex = 0;
        this.versionService.page(page);
        this.versionService.reload();
      }
      versions.forEach(v => {
        if (v.storePageIndex === undefined) v.storePageIndex = 0;
      });

      this.versions = [...versions];
      return { versions, isLoading, totalRecords, page, event };
    })
  );

  storesViewModel$ = combineLatest([
    // this.versionStoreService.unassignedStores$,
    this.versionStoreService.isLoading$,
    // this.versionStoreService.unassignedStoresTotalRecords$,
    // this.versionStoreService.unassignedStoresPage$
  ]).pipe(
    map(([isLoading]) => ({
      unassignedStores: <Store[]>[],
      isLoading: isLoading ?? false,
      totalRecords: 0,
      page: {}
    }))
  );

  constructor(
    private versionStoreService: VersionStoreService<any>,
    private versionService: VersionService<TVersion>,
    private eventService: EventService<TEvent>,
    private router: Router,
    private route: ActivatedRoute,
    private dialog: MatDialog,
    private matSnackBar: MatSnackBar) { }

  downloadVersionStoreSummary(versionIds?: string[]): void {
    const selectedVersionIds = versionIds ?? this.getSelectedSectionRecords(this.versions).map(v => v.Id);

    this.versionService.downloadVersionStoreSummary(this.eventId, selectedVersionIds).subscribe({
      next: res => {
        const contentDisposition = res.headers.get('content-disposition');
        let fileName = 'eventVersionStoreSummary.csv';

        if (contentDisposition) {
          const utf8FileNameMatch = contentDisposition.match(/filename\*\s*=\s*UTF-8''([^;]+)/);
          if (utf8FileNameMatch && utf8FileNameMatch[1]) {
            fileName = decodeURIComponent(utf8FileNameMatch[1]);
          } else {
            const fileNameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
            if (fileNameMatch && fileNameMatch[1]) {
              fileName = fileNameMatch[1];
            }
          }
        }

        const blob = new Blob([res.body!], { type: res.body!.type });
        const url = window.URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = fileName;
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
        window.URL.revokeObjectURL(url);
      },
      error: () => {
        this.matSnackBar.open('Failed to download CSV', 'OK', DEFAULT_SNACKBAR_CONFIG);
      }
    });
  }

  exportAllVersions(): void {
    this.downloadVersionStoreSummary([]);
  }

  exportSelectedVersions(): void {
    const selectedVersionIds = this.getSelectedSectionRecords(this.versions).map(v => v.Id);
    if (!selectedVersionIds.length) return;
    this.downloadVersionStoreSummary(selectedVersionIds);
  }

  selectAllVersions(checked: boolean): void {
    this.versions.forEach(v => v.selected = checked);
  }

  isAllVersionsSelected(): boolean {
    return this.versions.length > 0 && this.getSelectedSectionRecords(this.versions).length === this.versions.length;
  }

  ngOnInit(): void {
    this.versionService.reload();
    this.route.params.subscribe(params => {
      this.eventId = params['eventId'];
      if (this.route.snapshot.parent && this.route.snapshot.parent.parent) {
        this.versionService.eventId = this.route.snapshot.parent.parent.params['eventId'];
        this.versionStoreService.eventId = this.route.snapshot.parent.parent.params['eventId'];
      }
    });

    // this.storesViewModel$.subscribe(vm => {
    //   this.unassignedStores = vm.unassignedStores || [];
    //   this.totalUnassignedRecords = vm.totalRecords || 0;
    //   this.unassignedPage = vm.page?.pageIndex || 0;
    // });

    this.clearSearch();
  }

  openVersionSearchDialog(totalVersionCount: number): void {
    this.dialog.open(VersionSearchDialogComponent, {
      width: '90%',
      height: '90%',
      maxWidth: '1200px',
      panelClass: 'version-search-dialog-panel',
      data: { eventId: this.eventId, totalVersionCount },
    });
  }

  /** ---------------- SORT LOGIC ---------------- */
  openSortDialog() {
    const dialogRef = this.dialog.open(VersionStoreSortDialogComponent, {
      width: '400px',
      data: { sortBy: this.sortBy, sortDirection: this.sortDirection },
      restoreFocus: false
    });

    dialogRef.afterClosed().subscribe((result?: { sortBy: 'VersionName' | 'ClientKey'; sortDirection: 'asc' | 'desc' }) => {
      if (result) {
        // Update current state
        this.sortBy = result.sortBy;
        this.sortDirection = result.sortDirection;

        // Always pass a new Sort object to trigger API
        const sortState: Sort = { active: this.sortBy, direction: this.sortDirection };
        this.onSort(sortState);
      }
    });
  }

  onSort(sortState: Sort) {
    // Call your service to sort
    this.versionService.sort(sortState);
  }

  reloadVersion(version: Version) {
    version.isLoading = true;
    version.hasError = false;

    this.versionService.getStoresByVersionId(version.Id).pipe(
      retry(2),
      catchError(err => {
        version.isLoading = false;
        version.hasError = true;
        return of({ stores: [], count: 0 });
      })
    ).subscribe(({ stores, count }) => {
      version.stores = stores;
      version.storesCount = count;
      version.isLoading = false;
      version.hasError = false;
    });
  }

  drop(event: CdkDragDrop<Store[]>, targetVersion?: Version) {
    if (event.previousContainer === event.container) {
      // moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
      return;
    }

    const movedStore = event.previousContainer.data[event.previousIndex];
    if (!movedStore) return;
    movedStore.selected = false;

    const fromVersion = event.previousContainer.id.startsWith('version-')
      ? this.versions.find(v => v.stores.includes(movedStore))
      : undefined;

    const fromWasFullyLoaded = fromVersion ? this.isStoresFullyLoaded(fromVersion) : false;
    const toWasFullyLoaded = targetVersion ? this.isStoresFullyLoaded(targetVersion) : false;

    transferArrayItem(event.previousContainer.data, event.container.data, event.previousIndex, event.currentIndex);

    if (event.previousContainer.id === 'unassigned' && targetVersion) {
      // Unassigned → Version
      this.handleAssociation(targetVersion, movedStore, `assigned to ${targetVersion.VersionName}`, event, toWasFullyLoaded);
    } else if (event.container.id === 'unassigned' && fromVersion) {
      // Version → Unassigned
      this.handleDisassociation(fromVersion, movedStore, `removed from ${fromVersion.VersionName}`, event, fromWasFullyLoaded);
    } else if (fromVersion && targetVersion && fromVersion.Id !== targetVersion.Id) {
      // Version → Version
      this.handleMove(fromVersion, targetVersion, movedStore, event, fromWasFullyLoaded, toWasFullyLoaded);
    }
  }

  private handleAssociation(version: Version, store: Store, message: string, event: CdkDragDrop<Store[]>, wasFullyLoaded?: boolean) {
    this.showLoader(version);
    this.versionStoreService.associateVersionToStore(version.Id, [store]).pipe(
      switchMap(() => this.refreshStoresForVersion(version, wasFullyLoaded))
    ).subscribe({
      next: updated => {
        this.updateVersion(updated);
        // this.versionStoreService.reloadUnassignedStores();
        this.snack(`Store ${store.StoreName} ${message}`);
      },
      error: () => this.rollback(event)
    });
  }

  private handleDisassociation(version: Version, store: Store, message: string, event: CdkDragDrop<Store[]>, wasFullyLoaded?: boolean) {
    this.showLoader(version);
    this.versionStoreService.disassociateVersionToStore(version.Id, [store]).pipe(
      switchMap(() => this.refreshStoresForVersion(version, wasFullyLoaded))
    ).subscribe({
      next: updated => {
        this.updateVersion(updated);
        // this.versionStoreService.reloadUnassignedStores();
        this.snack(`Store ${store.StoreName} ${message}`);
      },
      error: () => this.rollback(event)
    });
  }

  private handleMove(fromVersion: Version, toVersion: Version, store: Store, event: CdkDragDrop<Store[]>, fromWasFullyLoaded: boolean, toWasFullyLoaded: boolean) {
    this.showLoader(fromVersion);
    this.showLoader(toVersion);
    this.versionStoreService.moveToVersion(fromVersion.Id, toVersion.Id, [store]).pipe(
      switchMap(() => this.refreshStoresForVersion(fromVersion, fromWasFullyLoaded)),
      tap(updatedFrom => {
        this.updateVersion(updatedFrom);
      }),
      switchMap(() => this.refreshStoresForVersion(toVersion, toWasFullyLoaded))
    ).subscribe({
      next: updatedTo => {
        this.updateVersion(updatedTo);
        this.snack(`Store ${store.StoreName} moved from ${fromVersion.VersionName} → ${toVersion.VersionName}`);
      },
      error: () => this.rollback(event)
    });
  }

  private updateVersion(updated: Version) {
    const idx = this.versions.findIndex(v => v.Id === updated.Id);
    if (idx > -1) {
      this.versions[idx] = updated;
      this.versions[idx].isLoading = false;
    }
  }

  private rollback(event: CdkDragDrop<Store[]>) {
    transferArrayItem(
      event.container.data,
      event.previousContainer.data,
      event.currentIndex,
      event.previousIndex
    );
  }

  private snack(msg: string) {
    this.matSnackBar.open(msg, 'OK', DEFAULT_SNACKBAR_CONFIG);
  }

  onAddStores(versionId: string) {
    let selectedVersions = this.versions.find(v => v.Id === versionId);
    if (selectedVersions) {
      const wasFullyLoaded = this.isStoresFullyLoaded(selectedVersions);
      const dialogRef = this.dialog.open(VersionStoreDialogComponent, {
        width: '80%',
        height: '90%',
        data: { versionId: versionId },
      });

      this.versionStoreService.dialogSearch('');

      dialogRef.afterClosed().subscribe((res) => {
        if (res && selectedVersions) {
          this.showLoader(selectedVersions);
          this.refreshStoresForVersion(selectedVersions, wasFullyLoaded).subscribe(updatedVersion => {
            selectedVersions = updatedVersion as TVersion;
            this.hideLoader(selectedVersions);
          });
        }
      });
    }
  }

  loadMoreStores(version: Version) {
    const nextPage = (version.storePageIndex ?? 0) + 1;
    this.showLoader(version);
    this.versionService.getStoresByVersionId(version.Id, nextPage).subscribe({
      next: ({ stores: newStores, count }) => {
        version.stores = [...version.stores, ...newStores];
        version.storePageIndex = nextPage;
        version.storesCount = count;
        this.hideLoader(version);
      },
      error: () => this.handleStoreLoadError(version),
    });
  }

  // loadAllStores(version: Version): void {
  //   const total = version.storesCount ?? 0;
  //   if ((version.stores?.length ?? 0) >= total) return;

  //   this.showLoader(version);
  //   this.versionService.getAllStoresByVersionId(version.Id, total).pipe(
  //     catchError(() => {
  //       this.handleStoreLoadError(version);
  //       return of({ stores: [], count: version.storesCount ?? 0 });
  //     })
  //   ).subscribe(({ stores, count }) => {
  //     if (stores.length > 0 || total === 0) {
  //       version.stores = stores;
  //       version.storesCount = count;
  //       version.storePageIndex = total > 0 ? Math.ceil(total / VERSION_STORE_PAGE_SIZE) - 1 : 0;
  //       version.hasError = false;
  //     }
  //     this.hideLoader(version);
  //   });
  // }

  hasMoreStores(version: Version): boolean {
    const loaded = version.stores?.length ?? 0;
    const total = version.storesCount ?? 0;
    return total > this.displayedCount && loaded < total;
  }

  private handleStoreLoadError(version: Version): void {
    this.hideLoader(version);
    this.matSnackBar.open('Failed to load stores', 'OK', DEFAULT_SNACKBAR_CONFIG);
  }

  trackByStoreId(_: number, store: Store): string {
    return store.Id;
  }

  isStoresFullyLoaded(version: Version): boolean {
    const loaded = version.stores?.length ?? 0;
    const total = version.storesCount ?? 0;
    return total > 0 && loaded >= total;
  }

  refreshStoresForVersion(version: Version, wasFullyLoaded?: boolean): Observable<Version> {
    const fullyLoaded = wasFullyLoaded ?? this.isStoresFullyLoaded(version);
    if (!fullyLoaded) {
      return this.getStoresForVersion(version, 0);
    }

    return this.versionService.getStoresByVersionId(version.Id, 0, VERSION_STORE_PAGE_SIZE).pipe(
      switchMap(({ count }) => {
        if (count === 0) {
          return of({ stores: [] as Store[], count: 0 });
        }
       // if (count <= VERSION_STORE_LOAD_ALL_CHUNK) {
          return this.versionService.getStoresByVersionId(version.Id, 0, count);
        //}
       // return this.versionService.getAllStoresByVersionId(version.Id, count);
      }),
      map(({ stores, count }) => {
        version.stores = [...stores];
        version.storesCount = count;
        version.storePageIndex = count > 0 ? Math.ceil(count / VERSION_STORE_PAGE_SIZE) - 1 : 0;
        return version;
      })
    );
  }

  getStoresForVersion(version: Version, pageIndex = 0): Observable<Version> {
    return this.versionService.getStoresByVersionId(version.Id, pageIndex).pipe(
      map(({ stores, count }) => {
        version.stores = [...stores];
        version.storePageIndex = pageIndex;
        version.storesCount = count;
        return version;
      })
    );
  }

  loadMoreUnassignedStores(currentPage: any) {
    const nextPage = {
      ...currentPage,
      pageIndex: currentPage.pageIndex + 1
    };
    this.versionStoreService.unassignedStorespage(nextPage);
  }

  removeStore(version: Version, store: Store) {
    const confirmDialog = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Confirm Remove',
        message: `Do you want to remove this store (${store.StoreName}) from the version?`,
      },
      disableClose: true,
    });

    confirmDialog.afterClosed().subscribe(confirmResult => {
      if (confirmResult) {
        this.showLoader(version);
        this.versionStoreService.disassociateVersionToStore(version.Id, [store]).pipe(
          tap(() => this.showLoader(version)),
          switchMap(() => this.refreshStoresForVersion(version))
        ).subscribe({
          next: updated => {
            this.updateVersion(updated);
            // this.versionStoreService.reloadUnassignedStores();
            this.matSnackBar.open(`Store removed from version`, 'OK', DEFAULT_SNACKBAR_CONFIG);
          },
          error: (error) => {
            console.error('Error disassociating store from version', error);
            this.matSnackBar.open(`Error removing store`, 'OK', DEFAULT_SNACKBAR_CONFIG);
            this.hideLoader(version);
          }
        });
      }
    });
  }

  getSelectedSectionRecords(versions: Version[]): Version[] {
    return versions?.filter(v => v.selected) ?? [];
  }

  getSelectedStoreRecords(version: Version): Store[] {
    return version?.stores?.filter(s => s.selected) ?? [];
  }

  trackById(index: number, item: Version): string {
    return item.Id;
  }

  moveToOtherVersion(version: Version): void {
    const selectedStores = this.getSelectedStoreRecords(version);
    if (selectedStores.length === 0) return;

    const dialogRef = this.dialog.open(VersionListDialogComponent, {
      width: '40%',
      height: '90%',
      data: { event: this.eventId, mode: VersionDialogMode.MOVE_STORES, excludedVersionIds: [version.Id] }
    });
    dialogRef.afterClosed().subscribe(targetVersion => {
      if (targetVersion && targetVersion.Id !== version.Id) {
        const fromWasFullyLoaded = this.isStoresFullyLoaded(version);
        const targetInList = this.versions.find(v => v.Id === targetVersion.Id);
        const toWasFullyLoaded = targetInList ? this.isStoresFullyLoaded(targetInList) : false;
        this.showLoader(version);
        if (targetInList) {
          this.showLoader(targetInList);
        }
        this.versionStoreService.moveToVersion(version.Id, targetVersion.Id, selectedStores).pipe(
          switchMap(() => this.refreshStoresForVersion(version, fromWasFullyLoaded)),
          tap(updatedFrom => {
            this.updateVersion(updatedFrom);
          }),
          switchMap(() => {
            const toVersion = this.versions.find(v => v.Id === targetVersion.Id) ?? targetVersion;
            return this.refreshStoresForVersion(toVersion, toWasFullyLoaded);
          })
        ).subscribe({
          next: updatedTo => {
            this.updateVersion(updatedTo);
            const message =
              selectedStores.length === 1
                ? `Store ${selectedStores[0].StoreName} moved from ${version.VersionName} → ${targetVersion.VersionName}`
                : `${selectedStores.length} stores moved from ${version.VersionName} → ${targetVersion.VersionName}`;

            this.snack(message);
          },
          error: error => {
            this.hideLoader(version);
            if (targetInList) {
              this.hideLoader(targetInList);
            }
            log.error('Error moving stores to new version', error);
            this.matSnackBar.open(`Failed to move stores`, 'Dismiss', DEFAULT_SNACKBAR_CONFIG);
          }
        });
      }
    });
  }

  moveToNewVersion(version: Version): void {
    const selectedStores = this.getSelectedStoreRecords(version);
    if (selectedStores.length === 0) return;

    this.goToDetails('0', version.Id, selectedStores);
  }

  disAssociateStore(version: Version) {
    const selectedStores = this.getSelectedStoreRecords(version);
    if (selectedStores.length === 0) return;

    const confirmDialog = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Confirm Delete',
        message: `Do you want to remove the selected ${selectedStores.length > 1 ? 'stores' : 'store'} from this version?`,
      },
      disableClose: true,
    });

    confirmDialog.afterClosed().subscribe(confirmResult => {
      if (confirmResult) {
        this.versionStoreService.disassociateVersionToStore(version.Id, selectedStores).pipe(
          tap(() => this.showLoader(version)),
          switchMap(() => this.refreshStoresForVersion(version))
        ).subscribe({
          next: updated => {
            this.updateVersion(updated);
            // this.versionStoreService.reloadUnassignedStores();
            this.matSnackBar.open(`${selectedStores.length > 1 ? 'Stores' : 'Store'} removed from version`, 'OK', DEFAULT_SNACKBAR_CONFIG);
          },
          error: (error) => {
            this.hideLoader(version);
            console.error(`Error disassociating ${selectedStores.length > 1 ? 'Stores' : 'Store'} from version`, error);
            this.matSnackBar.open(`Error disassociating ${selectedStores.length > 1 ? 'Stores' : 'Store'} from version`, 'OK', DEFAULT_SNACKBAR_CONFIG);
          }
        });
      }
    });
  }

  goToDetails(versionId: string, fromVersionId: string = '', stores: Store[] = []) {
    const dialogRef = this.dialog.open(VersionManagementDialogComponent, {
      width: '80%',
      height: '90%',
      data: { versionId: versionId, eventId: this.eventId, fromVersionId: fromVersionId, stores: stores },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result === 'saved' || result === 'deleted' || result === 'movedToVersion') {
        this.clearSelection();
      } else if (result === 'updated') {
        let _version = this.versions.find(v => v.Id === versionId);
        _version && this.reloadVersion(_version);
      }
      this.clearSearch();
      this.versionService.resetSort();
      this.versionService.reload();
    });
  }

  onAssociateStore(version: Version) {
    this.router.navigate([`${version.Id}/stores`], { relativeTo: this.route });
  }

  selectVersion(version: Version, checked: boolean) {
    version.selected = checked;
  }

  showLoader(version: Version): void {
    version.isLoading = true;
  }

  hideLoader(version: Version): void {
    version.isLoading = false;
  }

  deleteSelectedVersions() {
    const selectedVersions = this.getSelectedSectionRecords(this.versions);
    if (!selectedVersions.length) return;

    const versionsWithStores = selectedVersions.filter(v => v.stores?.length);
    const deletingAllVersions = selectedVersions.length === this.versions.length;
    const onlyOneVersionExists = this.versions.length === 1;

    if (versionsWithStores.length === 0 || deletingAllVersions || onlyOneVersionExists) {
      this.openconfirmDeleteDialog(selectedVersions);
    } else {
      this.openReassignDialog(selectedVersions);
    }
  }

  private openconfirmDeleteDialog(selectedVersions: any[]) {
    const confirmDialog = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Confirm Delete',
        message: `Do you want to remove this ${selectedVersions.length > 1 ? 'Versions' : 'Version'}?`
      },
      disableClose: true
    });

    confirmDialog.afterClosed().pipe(take(1)).subscribe(result => {
      if (!result) return;
      this.executeDelete(selectedVersions, null);
    });
  }

  private openReassignDialog(selectedVersions: any[]) {
    const dialogRef = this.dialog.open(VersionListDialogComponent, {
      width: '40%',
      height: '90%',
      data: {
        event: this.eventId,
        mode: VersionDialogMode.REASSIGN_ORPHANED,
        excludedVersionIds: selectedVersions.map(v => v.Id)
      }
    });

    dialogRef.afterClosed().pipe(take(1)).subscribe(targetVersion => {
      if (!targetVersion) return;
      this.executeDelete(selectedVersions, targetVersion.Id);
    });
  }

  private executeDelete(selectedVersions: any[], targetVersionId: string | null) {

    this.versionService.deleteVersions(selectedVersions, targetVersionId)
      .subscribe({
        next: () => {
          const baseMessage = selectedVersions.length > 1 ? `${selectedVersions.length} Versions` : selectedVersions[0].VersionName;
          const message = targetVersionId ? `${baseMessage} deleted and stores moved` : `${baseMessage} deleted`;
          this.matSnackBar.open(message, 'OK', DEFAULT_SNACKBAR_CONFIG);
          this.versionService.reload();
          this.clearSelection();
        },
        error: (error) => {
          log.error('Error occurred while deleting version', error);
          let errorMessage = 'Something went wrong while deleting the version.';
          if (error.status === 400) {
            errorMessage = error.error?.value || error.error?.message || 'Invalid request while deleting versions.';
          }
          if (error.status === 500) {
            errorMessage = 'Server error occurred. Please try again later.';
          }
          this.matSnackBar.open(errorMessage, 'Close', DEFAULT_SNACKBAR_CONFIG);
        }
      });
  }

  public clearSelection() {
    if (!this.versions) return;
    this.versions.forEach(v => v.selected = false);
  }

  onSearch(event: any) {
    this.versionService.search(event.target.value);
  }

  onPage(pageEvent: PageEvent): void {
    this.clearSelection();
    this.versionService.page(pageEvent);
  }

  clearSearch() {
    this.filtersForm.controls.search.setValue('');
    this.versionService.search('');
  }
}
